import {
  format,
  parseISO,
  isToday as fnsIsToday,
  isFuture as fnsIsFuture,
  isPast as fnsIsPast,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  isValid,
} from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { toZonedTime, format as formatTz } from 'date-fns-tz';

// Mapa de formatos de fecha del tenant a tokens de date-fns
const DATE_FORMAT_MAP: Record<string, string> = {
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'DD-MM-YYYY': 'dd-MM-yyyy',
};

const LOCALE_MAP: Record<string, typeof es | typeof enUS> = {
  es: es,
  en: enUS,
};

export interface DateFormatterConfig {
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  language: string;
}

export interface DateFormatterResult {
  formatDate: (date: Date | string | null | undefined) => string;
  formatTime: (date: Date | string | null | undefined) => string;
  formatDateTime: (date: Date | string | null | undefined) => string;
  formatDateShort: (date: Date | string | null | undefined) => string;
  formatDateLong: (date: Date | string | null | undefined) => string;
  formatRelativeTime: (date: Date | string | null | undefined) => string;
  formatDuration: (hours: number) => string;
  toLocalDate: (date: Date | string) => Date;
  isToday: (date: Date | string) => boolean;
  isFuture: (date: Date | string) => boolean;
  isPast: (date: Date | string) => boolean;
  config: DateFormatterConfig;
}

const parseDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  if (date instanceof Date) {
    return isValid(date) ? date : null;
  }
  try {
    const parsed = parseISO(date);
    return isValid(parsed) ? parsed : null;
  } catch {
    try {
      const fallback = new Date(date);
      return isValid(fallback) ? fallback : null;
    } catch {
      return null;
    }
  }
};

export const createDateFormatter = (
  config: DateFormatterConfig
): DateFormatterResult => {
  const locale = LOCALE_MAP[config.language] || es;
  const datePattern = DATE_FORMAT_MAP[config.dateFormat] || 'dd/MM/yyyy';
  const timePattern = config.timeFormat === '12h' ? 'h:mm a' : 'HH:mm';

  const toLocal = (date: Date): Date => {
    try {
      return toZonedTime(date, config.timezone);
    } catch {
      return date;
    }
  };

  return {
    formatDate: (date: Date | string | null | undefined): string => {
      const d = parseDate(date);
      if (!d) return '-';
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return format(d, datePattern, { locale });
      }
      try {
        return formatTz(toLocal(d), datePattern, {
          locale,
          timeZone: config.timezone,
        });
      } catch {
        return format(d, datePattern, { locale });
      }
    },

    formatTime: (date: Date | string | null | undefined): string => {
      const d = parseDate(date);
      if (!d) return '-';
      try {
        return formatTz(toLocal(d), timePattern, {
          locale,
          timeZone: config.timezone,
        });
      } catch {
        return format(d, timePattern, { locale });
      }
    },

    formatDateTime: (date: Date | string | null | undefined): string => {
      const d = parseDate(date);
      if (!d) return '-';
      try {
        return formatTz(toLocal(d), `${datePattern} ${timePattern}`, {
          locale,
          timeZone: config.timezone,
        });
      } catch {
        return format(d, `${datePattern} ${timePattern}`, { locale });
      }
    },

    formatDateShort: (date: Date | string | null | undefined): string => {
      const d = parseDate(date);
      if (!d) return '-';
      try {
        return formatTz(toLocal(d), 'd MMM', {
          locale,
          timeZone: config.timezone,
        });
      } catch {
        return format(d, 'd MMM', { locale });
      }
    },

    formatDateLong: (date: Date | string | null | undefined): string => {
      const d = parseDate(date);
      if (!d) return '-';
      const pattern =
        config.language === 'es'
          ? "d 'de' MMMM 'de' yyyy"
          : 'MMMM d, yyyy';
      try {
        return formatTz(toLocal(d), pattern, {
          locale,
          timeZone: config.timezone,
        });
      } catch {
        return format(d, pattern, { locale });
      }
    },

    formatRelativeTime: (date: Date | string | null | undefined): string => {
      const d = parseDate(date);
      if (!d) return '-';

      const now = new Date();
      const localDate = toLocal(d);
      const localNow = toLocal(now);

      const diffMins = Math.abs(differenceInMinutes(localNow, localDate));
      const diffHours = Math.abs(differenceInHours(localNow, localDate));
      const diffDays = Math.abs(differenceInDays(localNow, localDate));

      const messages =
        config.language === 'es'
          ? { now: 'Ahora', mins: 'min', hours: 'h', days: 'días', ago: 'Hace' }
          : {
              now: 'Now',
              mins: 'min',
              hours: 'h',
              days: 'days',
              ago: '',
            };

      const isPastDate = localDate < localNow;
      const prefix = isPastDate
        ? config.language === 'es'
          ? 'Hace '
          : ''
        : config.language === 'es'
          ? 'En '
          : 'In ';
      const suffix = isPastDate
        ? config.language === 'en'
          ? ' ago'
          : ''
        : '';

      if (diffMins < 1) return messages.now;
      if (diffMins < 60) return `${prefix}${diffMins} ${messages.mins}${suffix}`;
      if (diffHours < 24)
        return `${prefix}${diffHours} ${messages.hours}${suffix}`;
      if (diffDays < 7) return `${prefix}${diffDays} ${messages.days}${suffix}`;

      try {
        return formatTz(localDate, datePattern, {
          locale,
          timeZone: config.timezone,
        });
      } catch {
        return format(localDate, datePattern, { locale });
      }
    },

    formatDuration: (hours: number): string => {
      if (hours < 1) {
        const mins = Math.round(hours * 60);
        return `${mins} min`;
      }
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    },

    toLocalDate: (date: Date | string): Date => {
      const d = parseDate(date);
      return d ? toLocal(d) : new Date();
    },

    isToday: (date: Date | string): boolean => {
      const d = parseDate(date);
      if (!d) return false;
      const localDate = toLocal(d);
      const localNow = toLocal(new Date());
      return (
        localDate.getDate() === localNow.getDate() &&
        localDate.getMonth() === localNow.getMonth() &&
        localDate.getFullYear() === localNow.getFullYear()
      );
    },

    isFuture: (date: Date | string): boolean => {
      const d = parseDate(date);
      return d ? fnsIsFuture(d) : false;
    },

    isPast: (date: Date | string): boolean => {
      const d = parseDate(date);
      return d ? fnsIsPast(d) : false;
    },

    config,
  };
};

// Default config for server-side or fallback usage
export const DEFAULT_DATE_FORMATTER_CONFIG: DateFormatterConfig = {
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  timezone: 'America/Mexico_City',
  language: 'es',
};
